// ─────────────────────────────────────────────────────────────────────────
// The Spine — shared contracts for the first-run setup flow.
// See docs/OLMPS_SPINE_SPEC.md. Setup = ORDER run once at the Endeavour
// scale: O=TELOS (sector direction) · R=MoyRi (gear intake) · D=KYRS+TKNE
// (load the machine). Execute/Revise are the live daily loop, not setup.
//
// This file is the CONTRACT: data banks (shapeCascade.ts, sectorSurvey.ts,
// gearIntake.ts) and screens (SpineFlow.tsx, SpineTelos.tsx,
// GearIntakeFlow.tsx) all implement/import these shapes. Do not fork them.
// ─────────────────────────────────────────────────────────────────────────
import type { Gear, Sector } from '../domain/olmps';
import { GEAR_ANCHOR } from './ascension';

// ── §1 TELOS: the SHAPE cascade (Vision → Goal/deity → Aim+Protocol) ──────

/** One concrete recurring action inside an aim — the "protocol part". */
export interface SpineProtocolPart {
  id: string;
  text: string;
}

/** Aim + its protocol, consolidated into one tier (spec §1 path B). */
export interface SpineAimOption {
  id: string;
  text: string;
  protocol: SpineProtocolPart[];
}

/** One proficiency level of a sector deity — L1 and L2 are different skill sets. */
export interface SectorDeityLevel {
  level: number; // 1-based
  title: string; // e.g. "Hypnos I — the Regular Sleeper"
  skills: string[]; // what this level trains
  /** Proven recurring activities that drive acquisition — these auto-populate
   *  the KYRS "SHAPES" list when the deity is selected. */
  activities: string[];
}

/** A non-OLMPS deity owned by a sector; acquiring it IS the sector's Goal. */
export interface SectorDeity {
  id: string; // e.g. 'hypnos'
  name: string; // e.g. 'HYPNOS'
  /** The habit/identity being acquired, in plain language — this is what the
   *  user actually chooses (e.g. "The early riser"). The deity name is shown
   *  in parentheses after it, never on its own. */
  habit: string;
  domain: string; // one-line domain, e.g. 'Sleep & recovery'
  tagline: string;
  levels: SectorDeityLevel[]; // at least L1 and L2
}

/** The full option bank for one SHAPE sector's cascade. */
export interface SectorCascade {
  sector: Sector;
  /** Endeavour options — "Having…" form. */
  visions: { id: string; text: string }[];
  /** Goal tier — the sector's deities. */
  deities: SectorDeity[];
  /** Aim & Protocol tier (consolidated). */
  aims: SpineAimOption[];
}

// ── §1 TELOS: the sector survey (path A) ──────────────────────────────────

export const SECTOR_SURVEY_MIN = 5;
export const SECTOR_SURVEY_MAX = 20;

export interface SurveyOption {
  label: string;
  /** Which cascade entries this answer votes for (ids from SectorCascade). */
  visionIds?: string[];
  deityIds?: string[];
  aimIds?: string[];
  /** Vote weight, 1–3. */
  weight: number;
}

export interface SurveyQuestion {
  prompt: string;
  options: SurveyOption[];
}

/** Ranked suggestion produced by folding survey answers. */
export interface SectorSuggestion {
  visionId: string | null;
  deityId: string | null;
  aimIds: string[]; // up to 3, ranked
}

// ── §3 MoyRi: gear-grouped intake ─────────────────────────────────────────

export const GEAR_INTAKE_MIN = 3; // may stop here; accuracy warning
export const GEAR_INTAKE_BASELINE = 10; // standard
export const GEAR_INTAKE_MAX = 20; // most accurate diagnostic

export interface GearIntakeOption {
  label: string;
  /** −4..+4 contribution to the position seed. */
  score: number;
  /** `${toolId}:${maladyId}` — which tool's malady this answer leans toward. */
  leans?: string;
}

export interface GearIntakeQuestion {
  prompt: string;
  options: GearIntakeOption[];
}

/** Folded result of one gear table. */
export interface GearIntakeResult {
  gear: Gear;
  /** The gear's most-active malady: which DMT tool and which malady subtype. */
  toolId: string;
  subtype: string;
  /** Position seed −4..+4 for that tool. */
  position: number;
  /** How many questions were answered (3..20) — confidence signal. */
  depth: number;
}

/** Default DMT per gear — the DEMI-tier anchor, learned first, always
 *  carried; must reach L1 before the gear's TYTN/OLMPN tools advance.
 *  Sourced from ascension.ts's GEAR_TIERS — no duplicated roster here. */
export const GEAR_DEFAULT_DMT: Record<Gear, string> = { ...GEAR_ANCHOR };

/** "Level 1" in current engine terms: displayed ≥ +1, or past dormant/training. */
export function isDmtLevel1(displayed: number, acquisition: string): boolean {
  return displayed >= 1 || acquisition === 'emerging' || acquisition === 'stable' || acquisition === 'secured';
}

// ── Persistence: user_settings.spine (JSONB) ──────────────────────────────

export interface SpineSectorSetup {
  visionText: string; // the chosen/custom Endeavour
  deityId: string | null; // sector deity being acquired (Goal)
  deityLevel: number; // level being worked (1-based)
  aims: { text: string; protocol: string[] }[];
  done: boolean;
}

export interface SpineState {
  /** §1 — per-sector TELOS output. */
  telos: Partial<Record<Sector, SpineSectorSetup>>;
  /** §3 — per-gear MoyRi intake results. */
  moyri: Partial<Record<Gear, GearIntakeResult>>;
  /** §4 — KYRS: the SHAPES list id once created, and completion. */
  kyrs: { shapesListId: string | null; done: boolean };
  /** §4 — TKNE step completion (projects entered → Astra populated). */
  tkne: { done: boolean };
  /** Set when the whole spine has been run once. */
  completedAt: string | null;
}

export const EMPTY_SPINE: SpineState = {
  telos: {},
  moyri: {},
  kyrs: { shapesListId: null, done: false },
  tkne: { done: false },
  completedAt: null,
};

/** Which spine step comes next (drives the guided flow + ORACLE entry card).
 * V9: 'tkne' removed from the sequence — projects are not part of intake
 * (OLMPS_V9_SPEC §13 #5). The SpineState.tkne field is kept for stored-data
 * compat but never routes. */
export type SpineStep = 'telos' | 'moyri' | 'kyrs' | 'done';

export function nextSpineStep(s: SpineState | null | undefined): SpineStep {
  if (!s) return 'telos';
  const sectors: Sector[] = ['S', 'H', 'A', 'P', 'E'];
  if (!sectors.every((sec) => s.telos[sec]?.done)) return 'telos';
  const gears: Gear[] = ['orient', 'regulate', 'direct', 'execute', 'revise'];
  if (!gears.every((g) => s.moyri[g])) return 'moyri';
  if (!s.kyrs.done) return 'kyrs';
  return 'done';
}
